// "Show more" for the collection grid. The section renders the whole page of products (up to 250) in
// its final order and marks everything after the first batch with .grid__item--deferred; each click
// reveals the next batch. How many are shown is remembered per URL for the session and brought back
// when the shopper returns with Back, or when the filters bring back a view they had expanded.
// facets.js swaps the grid's HTML on every filter or sort change, which reconnects this element.

// Keeps grid cards from getting stuck invisible. Dawn's scroll animation (animations.js) stops watching a
// card once it has appeared, but when a fast scroll makes a card enter and leave within one frame it
// marks the card off-screen again afterwards, leaving it at zero opacity for good. This watcher shows
// such a card as soon as it is back in view.
window.productGridRevealWatcher =
  window.productGridRevealWatcher ||
  (() => {
    if (!('IntersectionObserver' in window)) return { watch() {} };
    const observer = new IntersectionObserver(
      (entries) => {
        const latest = new Map();
        entries.forEach((entry) => latest.set(entry.target, entry));
        latest.forEach((entry, card) => {
          if (entry.isIntersecting) card.classList.remove('scroll-trigger--offscreen');
        });
      },
      { rootMargin: '0px 0px -50px 0px' }
    );
    return {
      watch(root = document) {
        root.querySelectorAll('#product-grid .grid__item.scroll-trigger').forEach((card) => observer.observe(card));
      },
    };
  })();
window.productGridRevealWatcher.watch();

if (!customElements.get('product-grid-show-more')) {
  customElements.define(
    'product-grid-show-more',
    class ProductGridShowMore extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.grid = document.getElementById('product-grid');
        this.button = this.querySelector('button');
        this.status = this.querySelector('.product-grid-show-more__status');
        this.batchSize = parseInt(this.dataset.batchSize, 10) || 16;
        this.offset = parseInt(this.dataset.offset, 10) || 0;
        if (!this.grid || !this.button) return;

        this.button.addEventListener('click', () => this.showNext(true));
        window.productGridRevealWatcher.watch();

        // While the page is still loading this is a fresh page view: only a Back visit keeps the old
        // count. Later connections come from facets.js, which updates the URL right after swapping the
        // grid, so wait a tick before reading it.
        const pageLoading = document.readyState !== 'complete';
        if (pageLoading && !ProductGridShowMore.cameBack()) {
          this.forget();
          return;
        }
        queueMicrotask(() => this.restore());
      }

      static cameBack() {
        const entries = performance.getEntriesByType ? performance.getEntriesByType('navigation') : [];
        return entries.length > 0 && entries[0].type === 'back_forward';
      }

      get storageKey() {
        return `product-grid-shown:${window.location.pathname}${window.location.search}`;
      }

      items() {
        return Array.from(this.grid.querySelectorAll('.grid__item'));
      }

      deferredItems() {
        return Array.from(this.grid.querySelectorAll('.grid__item--deferred'));
      }

      showNext(moveFocus) {
        const revealed = this.deferredItems().slice(0, this.batchSize);
        revealed.forEach((item) => item.classList.remove('grid__item--deferred'));
        this.update();
        this.remember();

        if (moveFocus && revealed.length) {
          // Target the card's own link by id: each card also contains a hidden link without one.
          const link =
            revealed[0].querySelector('a[id^="CardLink-"]') ||
            revealed[0].querySelector('a[id^="StandardCardNoMediaLink-"]');
          if (link) link.focus();
        }
      }

      shownOnPage() {
        return this.items().length - this.deferredItems().length;
      }

      update() {
        const remaining = this.deferredItems().length;
        if (this.status && this.status.dataset.template) {
          this.status.textContent = this.status.dataset.template.replace('[shown]', this.offset + this.shownOnPage());
        }
        if (remaining === 0) this.classList.add('hidden');
      }

      remember() {
        try {
          sessionStorage.setItem(this.storageKey, String(this.shownOnPage()));
        } catch (e) {
          // Storage can be unavailable (private mode); the grid still works.
        }
      }

      forget() {
        try {
          sessionStorage.removeItem(this.storageKey);
        } catch (e) {
          // Nothing to forget without storage.
        }
      }

      restore() {
        let saved = 0;
        try {
          saved = parseInt(sessionStorage.getItem(this.storageKey), 10) || 0;
        } catch (e) {
          return;
        }
        const extra = saved - this.shownOnPage();
        if (extra <= 0) return;
        this.deferredItems()
          .slice(0, extra)
          .forEach((item) => item.classList.remove('grid__item--deferred'));
        this.update();
      }
    }
  );
}
